<template>
  <div>
    <panel :title="koConfig.paymentPacksTabName || 'Paczki płatności'">
      <div slot="toolbar">
        <w-button text="Filtruj" class="btn-light" @click="openModal('filters')" />
      </div>

      <w-table
        v-if="count > 0"
        entity-type="payment_pack"
        :per-page="-1"
        :hide-add-button="true"
        :hide-search-input="true"
        :actions="actions"
        :columns="columns"
        :data="list"
        :total="count"
        :foot-data="footData"
        @view="goToPayments"
      />

      <p v-else class="text-center mt-3"><b>Nie znaleziono wyników.</b></p>

      <div v-if="koEnabled" slot="footer" class="d-flex justify-content-end">
        <check-permission permissions="PAYMENT.UPDATE">
          <w-button
            text="Oznacz środki KO otrzymane"
            class="btn-primary ml-2"
            :disabled="!isAnyPaymentKoSent"
            @click="openModal('receivedFunds')"
          />
        </check-permission>

        <check-permission permissions="PAYMENT.UPDATE">
          <w-button
            text="Oznacz środki wypłacone"
            class="btn-primary ml-2"
            :disabled="!isAnyPaymentPackListSent"
            @click="openModal('paid')"
          />
        </check-permission>

        <check-permission permissions="PAYMENT.MDOK">
          <w-button text="Prześlij rozliczenie KO do MDOK" class="btn-primary ml-2" @click="openModal('koReport')" />
        </check-permission>
      </div>
    </panel>

    <payment-pack-select-multiple-with-date-modal
      v-if="modals.paid"
      :show="modals.paid"
      @close="closeModal('paid')"
      @submit="markAsPaid"
    />

    <payment-pack-select-multiple-with-date-modal
      v-if="modals.receivedFunds"
      modal-title="Oznacz środki KO otrzymane"
      date-title="Data otrzymania środków KO"
      :status="PaymentState.KO_SENT"
      :show="modals.receivedFunds"
      @close="closeModal('receivedFunds')"
      @submit="markAsReceivedFunds"
    />

    <ko-report-modal v-if="modals.koReport" :show="modals.koReport" @close="closeModal('koReport')" />

    <payment-pack-filters-modal v-if="modals.filters" :show="modals.filters" @close="closeModal('filters')" />
  </div>
</template>

<script>
import { mapState } from 'vuex';

import KoReportModal from '@/components/modals/KoReportModal';
import PaymentPackFiltersModal from '@/components/modals/PaymentPackFiltersModal.vue';
import PaymentPackSelectMultipleWithDateModal from '@/components/modals/PaymentPackSelectMultipleWithDateModal';
import Panel from '@/components/panel/Panel';
import WTable from '@/components/table/Table';
import { Errors } from '@/enums/errors.enum';
import { PaymentState, PaymentStateTranslations } from '@/enums/paymentState';
import { PaymentTypeTranslations } from '@/enums/paymentType';
import { PublicSettings } from '@/enums/publicSettings.enum';
import { Successes } from '@/enums/successes.enum';
import { date, dateTime } from '@/filters/date';
import { displayModalMixin } from '@/mixins/displayModal';
import { koMixin } from '@/mixins/ko';
import { listMixin } from '@/mixins/list';
import { notificationsMixin } from '@/mixins/notifications';
import { koResource } from '@/resources/ko.resource';
import { VIEW_ACTION } from '@/utils/constants';
import { calculateFootData } from '@/utils/footData';
import { preparePaymentPackQuery } from '@/utils/preparePaymentPackQuery';
import { serverError } from '@/utils/serverError';
import { formatAmount } from '@/utils/tables';

const messages = {
  listError: Errors.PAYMENT_PACK_CANNOT_LIST
};

// @vuese
// Lista paczek płatności
// @group Procedure
export default {
  components: { KoReportModal, Panel, PaymentPackFiltersModal, PaymentPackSelectMultipleWithDateModal, WTable },

  mixins: [displayModalMixin, koMixin, listMixin('paymentPack', messages), notificationsMixin],

  data() {
    return {
      actions: [{ ...VIEW_ACTION, permissions: ['PAYMENT.LIST'] }],
      defaultColumns: [
        { name: 'id', displayName: 'ID', sortable: true },
        { name: 'number', displayName: 'Numer', sortable: true },
        { name: 'created', displayName: 'Data utworzenia', nameTransformer: value => dateTime(value), sortable: true },
        {
          name: 'date',
          displayName: 'Termin płatności',
          nameTransformer: value => (value && date(value)) || '',
          sortable: true
        },
        {
          cssClass: 'text-right',
          displayName: 'Kwota paczki',
          name: 'total',
          nameTransformer: formatAmount,
          sortable: true
        },
        { name: 'externalId', displayName: 'ID KSAT', sortable: true },
        {
          name: 'systemNumbers',
          displayName: 'ID MDOK',
          nameTransformer: value => (value && (value.mdokId || value.mdokJoId)) || ''
        },
        {
          name: 'status',
          displayName: 'Status',
          sortable: true,
          nameTransformer: value => PaymentStateTranslations[value] || 'Nieznany status'
        },
        {
          name: 'type',
          displayName: 'Typ',
          nameTransformer: value => PaymentTypeTranslations[value] || 'Nieznany typ'
        },
        { name: 'structure.name', displayName: 'Jednostka', nameTransformer: value => value || 'OW' }
      ],
      PaymentState
    };
  },

  computed: {
    ...mapState({
      settingColumns: state =>
        state.setting.settings[PublicSettings.PAYMENT_PACK_COLUMNS]?.value &&
        JSON.parse(state.setting.settings[PublicSettings.PAYMENT_PACK_COLUMNS]?.value)
    }),

    columns() {
      return this.settingColumns || this.defaultColumns;
    },

    isAnyPaymentPackListSent() {
      return this.list.some(pack => pack.status === PaymentState.LIST_SENT);
    },

    isAnyPaymentKoSent() {
      return this.list.some(pack => pack.status === PaymentState.KO_SENT);
    },

    footData() {
      return calculateFootData(this.list, this.columns);
    }
  },

  methods: {
    // @vuese
    // Przechodzi do listy płatności danej paczki
    goToPayments(pack) {
      this.$router.push({ name: 'procedurePayments', params: { packId: pack.id } });
    },
    // @vuese
    // Filtry listy
    getFilters(route) {
      return preparePaymentPackQuery(route);
    },

    markAsPaid(data) {
      koResource
        .markMultipleAsPaid(data)
        .then(() => {
          this.notifySuccess(Successes.KO_PAYMENT_PACK_PAID);
          this.$store.dispatch('paymentPack/list', preparePaymentPackQuery(this.$route));
          this.closeModal('paid');
        })
        .catch(err => this.notifyError(serverError(err)));
    },

    markAsReceivedFunds(data) {
      koResource
        .markMultipleAsReceivedFunds(data)
        .then(() => {
          this.notifySuccess(Successes.KO_PAYMENT_PACK_RECEIVED_FUNDS);
          this.$store.dispatch('paymentPack/list', preparePaymentPackQuery(this.$route));
          this.closeModal('receivedFunds');
        })
        .catch(err => this.notifyError(serverError(err)));
    }
  }
};
</script>
